import { fetchWithAuth } from '@/lib/api/core'

/** CustomEvent name when OAuth popup reports a failure (opener listens). */
export const GOOGLE_DRIVE_AUTH_ERROR_EVENT = 'google-drive-auth-error'

export type GoogleDriveAccessRequestResult = {
  success: boolean
  alreadyRequested?: boolean
  inboxItemId?: string
  message?: string
  error?: string
}

/** Shared copy for prod Drive empty + harness auth mock. */
export const DRIVE_ACCESS_COPY = {
  blockedTitle: 'Google Drive access not enabled yet',
  blockedBody:
    'Maater Drive is still in Google Testing. Your Google account must be added as a test user before Connect can finish. Request access and an operator will add you (usually the same day).',
  needAccessLabel: 'Need access?',
  requestAccessLabel: 'Request access',
  retryConnectLabel: 'Retry Connect',
  notConnectedTitle: 'Google Drive not connected',
  notConnectedBody: 'Connect Google Drive to browse files, view comments, and sync documents.',
  reconnectTitle: 'Reconnect Google Drive',
  activityReconnectBody:
    'Reconnect to include file edits and Shared with me. Open comments still load.',
} as const

export const GOOGLE_DRIVE_REAUTH_CODE = 'GOOGLE_DRIVE_REAUTH_REQUIRED'

/** True when browse/drives/search mean the stored Google token must be reconnected. */
export function isGoogleDriveReauthResponse(
  res: Response,
  body?: { code?: string } | null,
): boolean {
  if (body?.code === GOOGLE_DRIVE_REAUTH_CODE) return true
  return res.status === 401
}

export async function openGoogleDriveOAuthPopup(organizationId?: string | null): Promise<void> {
  if (!organizationId) {
    throw new Error('Select a workspace before connecting Google Drive')
  }
  const res = await fetchWithAuth(
    `/api/google-drive/auth/url?organizationId=${encodeURIComponent(organizationId)}`,
  )
  if (!res.ok) throw new Error('Failed to get auth URL')
  const data = (await res.json()) as { url?: string }
  if (!data.url) throw new Error('Failed to get auth URL')
  window.open(data.url, 'google-drive-oauth', 'width=500,height=650,popup')
}

/** Google / callback error codes that mean the user is not on the test-user list (or similar). */
export function isGoogleDriveAccessBlockedError(code: string | null | undefined): boolean {
  if (!code) return false
  const c = code.toLowerCase()
  return (
    c === 'access_denied' ||
    c === 'access_blocked' ||
    c.includes('access_denied') ||
    c.includes('access_blocked') ||
    c.includes('unverified')
  )
}

export async function requestGoogleDriveAccess(params: {
  organizationId?: string | null
  googleEmail?: string | null
  reason?: string | null
  errorCode?: string | null
}): Promise<GoogleDriveAccessRequestResult> {
  const res = await fetchWithAuth('/api/google-drive/access-request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      organizationId: params.organizationId ?? undefined,
      googleEmail: params.googleEmail ?? undefined,
      reason: params.reason ?? undefined,
      errorCode: params.errorCode ?? undefined,
    }),
  })
  const body = (await res.json().catch(() => ({}))) as GoogleDriveAccessRequestResult
  if (!res.ok) {
    throw new Error(body.error || `Access request failed (HTTP ${res.status})`)
  }
  return body
}

/** Strip legacy ?google_drive_error= from the URL after reading. */
export function consumeLegacyGoogleDriveErrorParam(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const url = new URL(window.location.href)
    const err = url.searchParams.get('google_drive_error')
    if (!err) return null
    url.searchParams.delete('google_drive_error')
    window.history.replaceState({}, '', url.pathname + url.search + url.hash)
    return err
  } catch {
    return null
  }
}
